import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { Offer, Product } from '@chicare/shared';

import { useProduct } from '@/lib/queries';
import { AppBar, AppBarButton } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { Button } from '@/components/ui/Button';
import { PackBox } from '@/components/ui/PackBox';
import { OfferRow } from '@/components/ui/OfferRow';
import { AuthenticityStrip } from '@/components/ui/AuthenticityStrip';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { useCartCount } from '@/lib/cart';
import { productPath, useSector } from '@/lib/sector';
import { Icon } from '@/components/icons/Icon';
import { PriceHistory } from './PriceHistory';
import { VerifyPackSheet } from './VerifyPackSheet';

/**
 * The product page.
 *
 * §10 Phase 2: "product page with the offer ladder as hero + Authenticity
 * Strip". Both of those words are load-bearing.
 *
 * HERO means the ladder is above the drug facts, not below them. Eighteen
 * pharmacies sell the same paracetamol; seeing them ranked by price, distance
 * and expiry is what a marketplace is FOR, so the photograph supports the
 * ladder rather than dominating it.
 *
 * §11 desktop: three columns — media, facts, and a STICKY right rail holding
 * the ladder above the certificate. Neither the price comparison nor the
 * verification ever scrolls away while you compare.
 */
export function ProductScreen() {
  const { slug } = useParams<{ slug: string }>();
  const sector = useSector();
  const { data: product, isLoading, isError, refetch } = useProduct(slug);
  const [verifying, setVerifying] = useState(false);
  const cartCount = useCartCount();

  // ── A product is only ever read on its own side of the wall ──────────────
  //
  // /products/<a-vet-product> is reachable: an old bookmark, a link somebody
  // pasted, a search engine that indexed it before animal health existed. It
  // must not render there, because the shell around it would be blue, the
  // heading would say medicines, and the dosing on the page would be a cow's.
  //
  // Redirect rather than 404: the page the person wants exists, and refusing
  // to show it teaches them the link is broken rather than that they were in
  // the wrong shop.
  if (product && sector !== product.sector) {
    return <Navigate to={productPath(product)} replace />;
  }

  if (isError) {
    return (
      <>
        <AppBar back title="Product" />
        <ErrorState
          title="We couldn't load this product"
          body="It may no longer be listed by any pharmacy near you, or your connection dropped."
          action={
            <Button full icon="refresh" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      </>
    );
  }

  if (isLoading || !product) {
    return <ProductSkeleton />;
  }

  const best = product.offers[0];

  return (
    <>
      <AppBar
        back
        title={product.display_name}
        actions={
          <>
            {/* "Save for later" had nothing behind it, so it is gone until
                there is a saved list to add to. */}
            <AppBarButton icon="cart" label="Cart" to="/cart" dot={cartCount > 0} />
          </>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] px-0 pb-6 xl:px-6 xl:pt-4">
        <Breadcrumb product={product} />

        <div className="xl:grid xl:grid-cols-[300px_1fr_340px] xl:items-start xl:gap-[26px]">
          {/* -- Media ------------------------------------------------------ */}
          <div className="bg-white px-4 pb-4 pt-3.5 xl:bg-transparent xl:p-0">
            {/* Stacked on a phone, not side by side.
                
                A 104px square next to the title was the whole picture on the
                screen where somebody decides whether this is the medicine they
                were sent for — and checking a pack against a photograph is the
                point of having one. It is now the width of the screen. */}
            <div className="xl:block">
              <PackBox
                pack={product.pack_art}
                src={product.image}
                alt={product.display_name}
                large
                rounded="lg"
                className="mb-3.5 h-[260px] w-full sm:h-[320px] xl:mb-3 xl:h-[300px] xl:bg-white"
              />

              {/* Mobile: identity sits under the pack. Desktop: it moves to
                  the middle column, where there is room for it. */}
              <div className="min-w-0 xl:hidden">
                <h1 className="m-0 font-display text-[22px] font-bold leading-tight tracking-[-0.02em]">
                  {product.display_name}
                </h1>
                <p className="mt-0.5 text-xs text-muted">
                  {[product.form, product.pack_size, product.manufacturer].filter(Boolean).join(' · ')}
                </p>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <Chip tone="ok" readOnly>
                    NAFDAC
                  </Chip>
                  <Chip readOnly>
                    {product.is_pom
                      ? product.is_veterinary
                        ? 'Vet prescription only'
                        : 'Prescription only'
                      : 'OTC'}
                  </Chip>
                </div>
                {best ? (
                  <p className="mt-2.5 text-[11px] text-faint">
                    From{' '}
                    <b className="font-display text-lg font-bold tracking-[-0.02em] text-blue tabular">
                      {product.from_price_display}
                    </b>{' '}
                    · {product.offer_count} {product.is_veterinary ? 'sellers' : 'pharmacies'}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* -- Facts (desktop middle column) ------------------------------ */}
          <div className="px-4 xl:px-0">
            <div className="hidden xl:block">
              <h1 className="m-0 font-display text-[29px] font-bold leading-tight tracking-[-0.02em]">
                {product.display_name}
              </h1>
              <p className="mb-3 mt-1.5 text-[13.5px] text-muted">
                {[product.manufacturer, product.pack_size, product.category?.name]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <div className="mb-[22px] flex flex-wrap gap-2">
                <Chip tone="ok" readOnly>
                  NAFDAC {product.nafdac_reg_no}
                </Chip>
                <Chip readOnly>
                  {product.is_pom
                    ? product.is_veterinary
                      ? 'Veterinary prescription only'
                      : 'Prescription only'
                    : 'Over the counter'}
                </Chip>
                {product.category ? <Chip readOnly>{product.category.name}</Chip> : null}
              </div>
            </div>

            {/* -- The offer ladder. Mobile hero position: directly under the
                   identity block and above every drug fact. --------------- */}
            <section aria-label="Offers" className="pt-4 xl:hidden">
              <OfferLadder product={product} />
            </section>

            <div className="mt-3.5 xl:mt-0">
              <AuthenticityStrip
                authenticity={product.authenticity}
                onVerify={() => setVerifying(true)}
                checkedOn="24 Aug"
                className="xl:hidden"
              />
            </div>

            {product.veterinary ? <VeterinaryPanel facts={product.veterinary} /> : null}

            <div className="mt-3.5 space-y-2.5">
              {product.dosage || product.how_to_use ? (
                <Card>
                  <h2 className="m-0 mb-2.5 font-display text-sm font-bold tracking-[-0.01em]">
                    Dosage &amp; how to use
                  </h2>
                  {product.dosage ? (
                    <p className="m-0 text-[13px] leading-[1.7] text-muted">{product.dosage}</p>
                  ) : null}
                  {product.how_to_use ? (
                    <p className="mt-2.5 text-[13px] leading-[1.7] text-muted">
                      {product.how_to_use}
                    </p>
                  ) : null}
                </Card>
              ) : null}

              <div className="grid gap-2.5 xl:grid-cols-2">
                {product.warnings ? (
                  <Card>
                    <h2 className="m-0 mb-2 font-display text-sm font-bold tracking-[-0.01em]">
                      Warnings
                    </h2>
                    <p className="m-0 text-[11px] leading-[1.7] text-muted">{product.warnings}</p>
                  </Card>
                ) : null}
                {product.storage ? (
                  <Card>
                    <h2 className="m-0 mb-2 font-display text-sm font-bold tracking-[-0.01em]">
                      Storage
                    </h2>
                    <p className="m-0 text-[11px] leading-[1.7] text-muted">{product.storage}</p>
                  </Card>
                ) : null}
              </div>

              {product.contraindications ? (
                <Card>
                  <h2 className="m-0 mb-2 font-display text-sm font-bold tracking-[-0.01em]">
                    Contraindications
                  </h2>
                  <p className="m-0 text-[11px] leading-[1.7] text-muted">
                    {product.contraindications}
                  </p>
                </Card>
              ) : null}

              {product.price_history && product.price_history.length > 1 ? (
                <PriceHistory points={product.price_history} />
              ) : null}
            </div>
          </div>

          {/* -- Sticky rail: ladder above certificate (desktop) ------------ */}
          <aside className="hidden xl:sticky xl:top-4 xl:block">
            <Card className="mb-3.5 !p-4">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="m-0 font-display text-[14.5px] font-bold tracking-[-0.01em]">
                  {product.offer_count} pharmacies have this
                </h2>
                <span className="text-[11px] text-faint">by price</span>
              </div>
              <OfferLadder product={product} />
            </Card>

            <AuthenticityStrip
              authenticity={product.authenticity}
              onVerify={() => setVerifying(true)}
              checkedOn="24 Aug"
            />

            {best ? (
              <>
                <Button full icon="cart" className="mt-3.5">
                  Add to cart · {best.price_display}
                </Button>
                <p className="mt-3 flex items-center justify-center gap-2 text-[11px] text-faint">
                  {/* "before you pay" was only true for door capture, which is
                      the exception rather than the rule. The check is real;
                      its position relative to the charge is not. */}
                  Checked at your door, or refunded
                </p>
              </>
            ) : null}
          </aside>
        </div>
      </div>

      {/* §11: primary actions in the bottom third on mobile. */}
      {best ? (
        <div className="sticky bottom-0 z-10 border-t border-line bg-white px-4 pb-3.5 pt-3 shadow-[0_-4px_16px_rgb(8_19_43/0.06)] xl:hidden">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0">
              <div className="text-[11px] text-faint">{best.pharmacy.name}</div>
              <div className="font-display text-[19px] font-bold tracking-[-0.02em] tabular">
                {best.price_display}
              </div>
            </div>
            <Button full icon="cart" className="flex-1">
              Add to cart
            </Button>
          </div>
        </div>
      ) : null}

      {verifying ? <VerifyPackSheet onClose={() => setVerifying(false)} /> : null}
    </>
  );
}

/**
 * Everything a veterinary pack says that a human one does not.
 *
 * ── WHY IT SITS ABOVE THE DOSAGE ────────────────────────────────────────────
 *
 * Because "not for human use" after the dosing instructions is a warning that
 * arrives after the mistake. This block is the first thing under the price on a
 * veterinary product, in the same position and for the same reason as the
 * Authenticity Strip: it answers the question that has to be answered before
 * anything else on the page means what it appears to mean.
 *
 * ── AND WHY THE WITHDRAWAL PERIODS ARE A TABLE ──────────────────────────────
 *
 * A farmer treats a cow on Tuesday and has to know which day the milk can go
 * back in the churn. That is a number, looked up under pressure, and it belongs
 * in a grid with a label beside it — not in the middle of a paragraph of
 * guidance where it is one sentence among eight.
 */
function VeterinaryPanel({ facts }: { facts: NonNullable<Product['veterinary']> }) {
  return (
    <section
      data-testid="veterinary-panel"
      aria-label="Veterinary information"
      className="mt-3.5 overflow-hidden rounded-card border border-blue bg-white"
    >
      <p className="m-0 bg-blue px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white">
        Animal use only
      </p>

      <div className="px-4 py-3.5">
        <p className="m-0 text-[13px] font-medium leading-relaxed text-ink">
          {facts.not_for_human_use}
        </p>

        {facts.prescription_note ? (
          <p className="m-0 mt-2.5 border-t border-line pt-2.5 text-[12.5px] leading-relaxed text-muted">
            {facts.prescription_note}
          </p>
        ) : null}

        {facts.species.length > 0 ? (
          <div className="mt-3.5 border-t border-line pt-3">
            <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              Licensed for
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {facts.species.map((species) => (
                <span
                  key={species.key}
                  className="rounded-pill border border-line px-2.5 py-1 text-[12px] font-medium text-ink"
                >
                  {species.label}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {facts.withdrawal.length > 0 ? (
          <div className="mt-3.5 border-t border-line pt-3">
            <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              Withdrawal period
            </p>
            <p className="m-0 mt-1 text-[12px] leading-relaxed text-muted">
              How long after the last dose before this animal, its milk or its eggs may be sold or
              eaten.
            </p>

            <dl className="m-0 mt-2.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              {facts.withdrawal.map((period) => (
                <div key={period.label} className="contents">
                  <dt className="text-[12.5px] font-medium text-muted">{period.label}</dt>
                  <dd className="m-0 font-mono text-[13px] font-bold text-ink tabular">
                    {period.value}
                  </dd>
                </div>
              ))}
            </dl>

            {facts.withdrawal_note ? (
              <p className="m-0 mt-2.5 text-[12px] leading-relaxed text-ink">
                {facts.withdrawal_note}
              </p>
            ) : null}
          </div>
        ) : facts.treats_food_animals ? (
          // Silence is not "none". A food animal with no recorded withdrawal
          // period means we do not know it yet, and saying so is the only
          // honest option — printing nothing reads as "safe immediately".
          <p className="m-0 mt-3.5 border-t border-line pt-3 text-[12.5px] leading-relaxed text-ink">
            We do not have a withdrawal period recorded for this product yet. Check the pack insert
            and ask your veterinarian before selling meat, milk or eggs from a treated animal.
          </p>
        ) : null}
      </div>
    </section>
  );
}

function OfferLadder({ product }: { product: Product }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? product.offers : product.offers.slice(0, 3);

  if (product.offers.length === 0) {
    return (
      <Card className="text-center">
        <p className="m-0 text-xs text-muted">
          No pharmacy near you has this in stock with enough shelf life left.
        </p>
      </Card>
    );
  }

  return (
    <>
      <div className="mb-2.5 flex items-baseline justify-between xl:hidden">
        <h2 className="m-0 font-display text-[14.5px] font-bold tracking-[-0.01em]">
          Best offers near you
        </h2>
        <span className="text-[11px] text-faint">by price</span>
      </div>

      <ul className="m-0 list-none space-y-1.5 p-0">
        {visible.map((offer: Offer) => (
          <li key={offer.id}>
            <OfferRow offer={offer} onAdd={() => undefined} />
          </li>
        ))}
      </ul>

      {product.offers.length > 3 ? (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="touch-target mt-3 flex w-full items-center justify-center gap-1.5 text-[12.5px] font-bold text-blue"
        >
          {expanded ? 'Show fewer offers' : `View all ${product.offer_count} offers`}
          <Icon name={expanded ? 'up' : 'chevron'} size={13} strokeWidth={2.4} />
        </button>
      ) : null}
    </>
  );
}

function Breadcrumb({ product }: { product: Product }) {
  return (
    <p className="hidden px-0 pb-4 text-[11px] text-faint xl:block">
      {/* The root of the trail names the SHOP, not a category. A veterinary
          product sitting under a crumb that reads "Medicines" is the page
          telling somebody they are in the human end while they read a cow's
          dose. */}
      {product.is_veterinary ? 'Veterinary' : 'Pharmacy'}{' '}
      <span className="text-faint">/</span> {product.category?.name}{' '}
      <span className="text-faint">/</span> <b className="text-ink">{product.display_name}</b>
    </p>
  );
}

function ProductSkeleton() {
  return (
    <>
      <AppBar back title=" " />
      <div className="mx-auto w-full max-w-[1200px] xl:px-6 xl:pt-4">
        <div className="bg-white px-4 pb-4 pt-3.5">
          <div className="flex gap-3.5">
            <Skeleton className="h-[104px] w-[104px] shrink-0" radius="md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-6 w-24" radius="pill" />
            </div>
          </div>
        </div>

        <div className="space-y-1.5 px-4 pt-4">
          <Skeleton className="h-3 w-40" />
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-[86px] w-full" radius="md" />
          ))}
        </div>

        <div className="px-4 pt-3.5">
          <Skeleton className="h-[190px] w-full" radius="card" />
        </div>

        <div className="px-4 pt-3.5">
          <div className="rounded-card border border-line bg-white p-3.5">
            <SkeletonText lines={4} />
          </div>
        </div>
      </div>
    </>
  );
}
